<template>
  <div
    v-if="showMessaging"
    class="product-stock-message"
  >
    <icon-low-stock />
    <p
      class="text-body-xs-regular"
      v-text="message"
    />
  </div>
</template>

<script>

/**
 * Vue: Product stock message (product-stock-message)
 * -----------------------------------------------------------------------------
 * Displays messaging about product and variant stock levels.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean} enabled - Whether low stock messaging is enabled.
 * @param {Number} threshold - Low stock threshold.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 */
import { handlePreOrderProductCurrentDate, handlePreOrderProductFutureDateTime, handlePreOrderProductPastDate } from '~/helpers/general'
import IconLowStock from '~icons/general/low-stock.svg'
export default {
  name: 'ProductStockMessage',

  components: {
    IconLowStock,
  },

  props: {
    enabled: {
      type: Boolean,
      default: true,
    },
    threshold: {
      type: Number,
      default: 50,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  computed: {

    /**
     * Compute messaging.
     * @returns {String}
     */
    message() {
      return this.$string('product.low_stock_message', {
        replace: {
          threshold: this.threshold,
          count: this.stockLevel,
        },
      })
    },

    /**
     * Is pre-order ended.
     * @returns {Boolean}
     */
    isPreOrderEnded() {
      return handlePreOrderProductCurrentDate(this.product?.metafields?.preOrderClosingDate) ||
        handlePreOrderProductPastDate(this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Compute checks for coming soon product.
     * @returns {Boolean}
     */
    isComingSoon() {
      const hasPreOrderTag = this.product?.tags?.some((tag) => tag.includes('trade-pre-order'))
      const hasAvailableToBuyDate = this.product?.metafields?.availableToBuyDate &&
        // eslint-disable-next-line max-len
        handlePreOrderProductFutureDateTime(this.product?.metafields?.availableToBuyDate, this.product?.currentDateTime)

      return !hasPreOrderTag && hasAvailableToBuyDate
    },

    /**
     * Compute whether to display messaging.
     * @returns {Boolean}
     */
    showMessaging() {
      return !this.isPreOrderEnded && this.enabled && this.stockLevel <= this.threshold &&
        this.stockLevel > 0 && !this.isComingSoon
    },

    /**
     * Compute current variant stock level
     * @returns {Number}
     */
    stockLevel() {
      if (this.variant) {
        return this.variant.inventory ?? 0
      }

      const firstVariant = this.product?.variants?.[0]
      return firstVariant?.inventory ?? 0
    },
  },
}
</script>

<style lang="scss">
@import './product-stock-message';
</style>
